import * as THREE from 'three';

export interface SpawnSlot {
  x: number;
  z: number;
}

// Fixed spawn lanes so targets pop up from behind consistent cover pieces,
// like a real shooting gallery rather than random floating positions.
export const DESERT_SPAWN_SLOTS: SpawnSlot[] = [
  { x: -6, z: 10 },
  { x: -3.5, z: 12 },
  { x: -1, z: 9 },
  { x: 1.5, z: 13 },
  { x: 4, z: 10.5 },
  { x: 6.5, z: 12 },
  { x: -4.5, z: 16 },
  { x: 0, z: 17 },
  { x: 4.5, z: 16 },
];

function buildSkyGradient(): THREE.Mesh {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = 2;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const gradient = ctx.createLinearGradient(0, 0, 0, size);
  gradient.addColorStop(0, '#2b1440'); // dusk purple at zenith
  gradient.addColorStop(0.45, '#7a3b5e');
  gradient.addColorStop(0.7, '#e0623f'); // sunset orange band
  gradient.addColorStop(0.86, '#ffb15c');
  gradient.addColorStop(1, '#ffd98a'); // bright horizon glow
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 2, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;

  const geo = new THREE.SphereGeometry(200, 16, 16);
  const mat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.BackSide, fog: false });
  return new THREE.Mesh(geo, mat);
}

function lowPolyRock(x: number, z: number, scale: number): THREE.Mesh {
  const geo = new THREE.IcosahedronGeometry(1, 0);
  // Squash it a bit for a "worn boulder" silhouette instead of a perfect gem.
  geo.scale(1, 0.6, 1);
  const mat = new THREE.MeshLambertMaterial({ color: 0x8a6a4d });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(x, 0.5 * scale, z);
  mesh.scale.setScalar(scale);
  mesh.rotation.y = Math.random() * Math.PI;
  return mesh;
}

function sandbagRow(x: number, z: number): THREE.Group {
  const group = new THREE.Group();
  const mat = new THREE.MeshLambertMaterial({ color: 0xc2a05c });
  for (let i = 0; i < 4; i++) {
    const geo = new THREE.BoxGeometry(0.55, 0.35, 0.4);
    const bag = new THREE.Mesh(geo, mat);
    bag.position.set(x + i * 0.5 - 0.75, 0.18, z);
    bag.rotation.y = (Math.random() - 0.5) * 0.15;
    group.add(bag);
  }
  return group;
}

function oilBarrel(x: number, z: number): THREE.Mesh {
  const geo = new THREE.CylinderGeometry(0.35, 0.35, 0.9, 8);
  const mat = new THREE.MeshLambertMaterial({ color: 0x5c3a2e });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(x, 0.45, z);
  return mesh;
}

function fencePost(x: number, z: number): THREE.Mesh {
  const geo = new THREE.CylinderGeometry(0.05, 0.06, 1.4, 5);
  const mat = new THREE.MeshLambertMaterial({ color: 0x6b5238 });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(x, 0.7, z);
  return mesh;
}

export function buildDesertRange(scene: THREE.Scene): void {
  scene.background = new THREE.Color(0xe0623f);
  scene.fog = new THREE.Fog(0xd88a5a, 8, 34); // authentic PS1-style draw-distance fog

  scene.add(buildSkyGradient());

  // Sun: a plain glowing disc, unaffected by lighting, low on the horizon.
  const sunGeo = new THREE.CircleGeometry(4, 24);
  const sunMat = new THREE.MeshBasicMaterial({ color: 0xffe4a3, fog: false });
  const sun = new THREE.Mesh(sunGeo, sunMat);
  sun.position.set(0, 6, 80);
  scene.add(sun);

  // Ground: flat low-poly sand plane with a subtle warm color.
  const groundGeo = new THREE.PlaneGeometry(80, 80, 1, 1);
  const groundMat = new THREE.MeshLambertMaterial({ color: 0xcf9f5f });
  const ground = new THREE.Mesh(groundGeo, groundMat);
  ground.rotation.x = -Math.PI / 2;
  scene.add(ground);

  // Lighting: warm ambient + low-angle "sunset" directional light.
  const ambient = new THREE.AmbientLight(0xffb37b, 0.75);
  scene.add(ambient);
  const sunLight = new THREE.DirectionalLight(0xff9a52, 1.1);
  sunLight.position.set(-10, 8, 6);
  scene.add(sunLight);
  const fillLight = new THREE.DirectionalLight(0x8b6fae, 0.3);
  fillLight.position.set(8, 5, -10);
  scene.add(fillLight);

  // Cover props in front of each spawn slot, matching the "pop up from
  // behind cover" arcade shooting-gallery read.
  DESERT_SPAWN_SLOTS.forEach((slot, i) => {
    if (i % 3 === 0) {
      scene.add(sandbagRow(slot.x, slot.z + 0.3));
    } else if (i % 3 === 1) {
      scene.add(oilBarrel(slot.x, slot.z + 0.3));
    } else {
      scene.add(lowPolyRock(slot.x, slot.z + 0.3, 0.7));
    }
  });

  // Scattered background dressing beyond the play lanes.
  for (let i = 0; i < 10; i++) {
    const x = (Math.random() - 0.5) * 40;
    const z = 20 + Math.random() * 15;
    scene.add(lowPolyRock(x, z, 0.4 + Math.random() * 0.8));
  }

  // A simple perimeter fence to frame the range.
  for (let x = -9; x <= 9; x += 2.2) {
    scene.add(fencePost(x, 22));
  }
}
